import { RefObject, useEffect } from 'react';
import queryString from 'query-string';
import {
  PermissionEnum,
  SchemaFilterType,
  useFilters,
  useGetList,
  usePrevious,
  useQueryState,
  useScroll,
} from '@ui/src';
import { useAppDispatch } from '@store';
import {
  LinkedClient,
  PspIntegration,
  PspIntegrationPayment,
  PspPartner,
} from '../../types';
import { filtersSchema, schema } from '../variables/list';
import {
  useLazyGetAccountsLinkedClientsQuery,
  useLazyGetAccountsPspPartnersQuery,
  useLazyGetPspIntegrationsQuery,
} from '../../store/services';
import {
  setLinkedClientTakeCount,
  clearLinkedClientsState,
} from '../../store/slice';
import useGetOrganizations from '../../hooks/use-get-organizations';

const useLinkedClientsList = ({
  scrollRef,
  pspPartners,
  linkedClients,
  search,
  takeCount,
  setTakeCount,
  pspIntegrations,
  activePermissions,
}: {
  scrollRef: RefObject<HTMLDivElement | null>;
  pspPartners?: PspPartner[];
  linkedClients?: LinkedClient[];
  search?: string;
  takeCount: number;
  setTakeCount: (data: number) => void;
  pspIntegrations?: PspIntegration[];
  activePermissions?: PermissionEnum[];
}) => {
  const dispatch = useAppDispatch();

  const [getLinkedClients, { isLoading, isFetching }] =
    useLazyGetAccountsLinkedClientsQuery();
  const [getAccountsPspPartners] = useLazyGetAccountsPspPartnersQuery();
  const [getPspIntegrations] = useLazyGetPspIntegrationsQuery();

  const { filters, onSortChange, onFilterChange } = useQueryState(schema);

  const prevFilters = usePrevious(filters);

  const { getListAction, onFilter, setPage, errorList } =
    useGetList<LinkedClient>({
      getList: getLinkedClients,
      onSortChange,
      onFilterChange,
      schema,
    });

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  const {
    autocompleteList,
    onSearch,
    isLoading: organizationsIsLoading,
    onClear,
    getAutocompleteListAction,
    getOrganizationById,
    selectedValue,
    setSelectedValue,
  } = useGetOrganizations();

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (
      !pspIntegrations &&
      activePermissions?.includes(PermissionEnum.PSP_LIST)
    ) {
      getPspIntegrations();
    }
  }, [pspIntegrations]);

  useEffect(() => {
    if (!pspPartners) {
      getAccountsPspPartners();
    }
  }, [pspPartners]);

  useEffect(() => {
    if (pspPartners && pspIntegrations) {
      const nextPspPartners = pspPartners.map((item) => {
        const result = { ...item };

        if (
          result.pspIntegrationId &&
          pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          )
        ) {
          const currentItegration = pspIntegrations?.find(
            (integration) => integration.id === result.pspIntegrationId,
          );

          if (currentItegration) {
            result.pspIntegration = {
              name: currentItegration.name,
              pspIntegrationPayments:
                currentItegration.pspIntegrationPayments as PspIntegrationPayment[],
              id: currentItegration.id,
              externalCustomersSupported:
                currentItegration.externalCustomersSupported,
            };
          }
        }

        return result;
      });

      const pspPartnersWithExternalCustomerSupport = nextPspPartners?.filter(
        (item) => item?.pspIntegration?.externalCustomersSupported,
      );

      if (pspPartnersWithExternalCustomerSupport?.length) {
        setValues(
          'pspPartnerId',
          pspPartnersWithExternalCustomerSupport?.map((item) => ({
            name: item.companyName,
            value: item.id,
          })),
        );
      }
    }
  }, [pspPartners, pspIntegrations]);

  useEffect(() => {
    if (
      !linkedClients &&
      !search &&
      !Object.keys(filters).length &&
      takeCount !== 0 &&
      activePermissions?.includes(PermissionEnum.EXTERNAL_ORGANIZATIONS_LIST)
    ) {
      getListAction({
        filters: {
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
        },
      });
    }
  }, [filters, search, linkedClients, takeCount, activePermissions]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        setTakeCount(Math.ceil(take + 3));

        dispatch(setLinkedClientTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (
      takeCount !== 0 &&
      !linkedClients &&
      search &&
      filters &&
      prevFilters &&
      Object.keys(prevFilters).length &&
      activePermissions?.includes(PermissionEnum.EXTERNAL_ORGANIZATIONS_LIST)
    ) {
      const filtersValues: any = {
        ...filters,
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
      };

      getListAction({
        filters: filtersValues,
      });

      if (autocompleteList && search) {
        const parsed = queryString.parse(search);

        if (parsed && parsed.organizationId) {
          if (
            !autocompleteList?.find(
              (item) => item.value === parsed.organizationId,
            )
          ) {
            getOrganizationById(parsed.organizationId as string);
          } else if (
            autocompleteList?.find(
              (item) => item.value === parsed.organizationId,
            )
          ) {
            setSelectedValue(parsed.organizationId as string);
          }
        }
      }
    }
  }, [
    filters,
    search,
    linkedClients,
    prevFilters,
    takeCount,
    activePermissions,
  ]);

  useEffect(
    () => () => {
      dispatch(clearLinkedClientsState());
    },
    [],
  );

  return {
    filters,
    autocompleteList,
    organizationsIsLoading,
    onSearch,
    getAutocompleteListAction,
    onFilter,
    onClear,
    errorList,
    filtersComponent,
    handleSubmit,
    isLoading,
    isFetching,
    setPage,
    selectedValue,
    setSelectedValue,
  };
};

export default useLinkedClientsList;
